import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, Badge, Button } from 'react-bootstrap';

import { getEcosystemDestinationUrl } from '@/shared/utils/ecosystemRouting';
import useCapability from '@/shared/hooks/useCapability';

import MentorProfileForm from './MentorProfileForm';
import AffiliationManager from './AffiliationManager';

/**
 * EcosystemMembershipAccordion: a single accordion item that wraps the
 * per-ecosystem editable surface (mentor profile + affiliations) for ONE of
 * the user's ecosystem memberships.
 *
 * Two modes:
 *
 *   1. ACTIVE membership (the ecosystem the page is currently scoped to):
 *      renders MentorProfileForm + AffiliationManager inline. These forms
 *      use tenant-scoped API endpoints via the axios singleton, which is
 *      pinned to the active ecosystem by EcosystemProvider on boot. The
 *      accordion is expanded by default in this mode.
 *
 *   2. OTHER membership (a different tenant): renders a summary + a "Switch
 *      to {ecosystem.name}" CTA that does a FULL RELOAD via
 *      getEcosystemDestinationUrl. Cross-tenant React Router navigation is
 *      unsafe because EcosystemProvider boots once with `useEffect(..., [])`
 *      and would not re-bootstrap on a SPA push. Editing other ecosystems'
 *      per-ecosystem state inline here would require running each
 *      accordion's API calls under a different basePath than the page's
 *      singleton: same hazard, so we instead make the cross-tenant trip
 *      explicit and route the user there.
 *
 * Header always shows: ecosystem name + role badge + joined date.
 */
export default function EcosystemMembershipAccordion({
    membership,
    isActive,
    profile,
    onProfileUpdate,
    eventKey,
}) {
    const { ecosystem, role, joinedAt } = membership;
    const joinedDate = joinedAt
        ? new Date(joinedAt).toLocaleDateString()
        : 'unknown date';

    // The active accordion edits the page-resolved ecosystem, so useCapability
    // reflects this tenant's flags. Hide the mentor form / affiliation manager
    // when the matching capability is off: the server endpoints 404 in that
    // case, so rendering the forms would surface a failing save.
    const mentoringEnabled = useCapability('mentoring');
    const affiliationsEnabled = useCapability('affiliations');

    return (
        <Accordion.Item
            eventKey={eventKey}
            data-testid={`membership-accordion-${ecosystem.slug}`}
        >
            <Accordion.Header>
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <strong>{ecosystem.name}</strong>
                    <Badge bg={role === 'admin' ? 'info' : 'secondary'}>
                        {role}
                    </Badge>
                    <small className="text-muted">Joined {joinedDate}</small>
                </div>
            </Accordion.Header>
            <Accordion.Body className="p-3">
                {isActive ? (
                    <>
                        {mentoringEnabled || affiliationsEnabled ? (
                            <p className="text-muted small mb-3">
                                Your profile settings in{' '}
                                <strong>{ecosystem.name}</strong>. These are
                                independent from your profile in any other
                                ecosystem you belong to.
                            </p>
                        ) : (
                            <p className="text-muted small mb-0">
                                This ecosystem has no per-membership profile
                                settings enabled.
                            </p>
                        )}
                        {mentoringEnabled && (
                            <div id="mentor">
                                <MentorProfileForm
                                    profile={profile}
                                    onUpdate={onProfileUpdate}
                                    ecosystem={ecosystem}
                                />
                            </div>
                        )}
                        {affiliationsEnabled && (
                            <div id="affiliations">
                                <AffiliationManager ecosystem={ecosystem} />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-3">
                        <p className="text-muted mb-3">
                            Your mentor profile and affiliations in{' '}
                            <strong>{ecosystem.name}</strong> are managed in
                            that ecosystem.
                        </p>
                        <Button
                            variant="outline-primary"
                            className="rounded-pill"
                            href={
                                getEcosystemDestinationUrl(ecosystem) +
                                '/profile'
                            }
                        >
                            Switch to {ecosystem.name}
                        </Button>
                    </div>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
}

EcosystemMembershipAccordion.propTypes = {
    membership: PropTypes.shape({
        ecosystem: PropTypes.shape({
            slug: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            customDomain: PropTypes.string,
            subdomain: PropTypes.string,
        }).isRequired,
        role: PropTypes.string,
        joinedAt: PropTypes.string,
    }).isRequired,
    isActive: PropTypes.bool.isRequired,
    profile: PropTypes.object,
    onProfileUpdate: PropTypes.func,
    eventKey: PropTypes.string.isRequired,
};
